import { baseFetch as fetch } from '@ixigo/fetch';
import type {
	Coords,
	FormattedAddress,
	LocationErrorCode,
	LocationOptions,
} from './types';
import { LocationError } from './types';

/**
 * Custom location provider function type
 * Returns coordinates or undefined if location is not available
 */
export type LocationProvider = () => Promise<Coords | undefined>;

// Global configuration
const globalConfig: {
	current?: LocationConfig;
} = {};

export interface LocationConfig {
	/**
	 * Google Maps API key
	 */
	apiKey: string;
	/**
	 * Optional custom location provider (e.g., for PWA)
	 * If provided, this will be used instead of browser geolocation for location bias
	 */
	locationProvider?: LocationProvider;
}

/**
 * Configure location services globally
 */
export function configure(options: LocationConfig): void {
	globalConfig.current = options;
}

/**
 * Get current global config (for internal use by other modules)
 */
export function getConfig() {
	return globalConfig.current;
}

/**
 * Get the configured location provider (for internal use)
 */
export function getLocationProvider(): LocationProvider | undefined {
	return globalConfig.current?.locationProvider;
}

/**
 * Creates a cached version of an async function
 * The result promise is cached and reused for all subsequent calls
 * @param fn - The async function to cache
 * @returns A function that returns the cached promise
 */
function withResultCache<T>(fn: () => Promise<T>): () => Promise<T> {
	let cachedPromise: Promise<T> | undefined;

	return () => {
		if (!cachedPromise) {
			cachedPromise = fn();
		}
		return cachedPromise;
	};
}

/**
 * Get user location with caching and fallback
 * 1. Tries custom location provider (if configured)
 * 2. Falls back to getCurrentLocation with ip-fallback if provider fails or returns nothing
 * 3. Caches the promise for subsequent calls
 *
 * Used internally by autocomplete and permission requests
 */
export const getUserLocation = /*#__PURE__*/ withResultCache(
	async (): Promise<Coords | undefined> => {
		const customProvider = getLocationProvider();

		if (customProvider) {
			try {
				const location = await customProvider();
				if (location) {
					return location;
				}
				// Custom provider returned undefined, fall back to browser
			} catch {
				// Custom provider failed, fall back to browser
			}
		}

		// Fall back to browser geolocation with IP fallback
		try {
			return await getCurrentLocation({
				detection: 'ip-fallback',
			});
		} catch {
			return undefined;
		}
	},
);

/**
 * Get API key from global config or override
 */
function getApiKey(override?: string): string {
	const apiKey = override || globalConfig.current?.apiKey;
	if (!apiKey) {
		throw new Error('Google Maps API key not configured');
	}
	return apiKey;
}

/**
 * Check if geolocation is supported by the browser
 */
export function isGeolocationSupported(): boolean {
	return 'geolocation' in navigator;
}

/**
 * Check if geolocation permission is already granted (does not prompt)
 */
export async function getPermissionStatus(): Promise<
	PermissionState | undefined
> {
	try {
		const status = await navigator.permissions?.query({
			name: 'geolocation' as PermissionName,
		});
		return status?.state;
	} catch {
		return undefined;
	}
}

/**
 * Fetch coarse location using Cloudflare IP headers
 */
export async function getLocationFromIp(): Promise<Coords | null> {
	try {
		const res = await fetch
			.get('hit', { prefixUrl: 'https://plan-cf.ixigo.com' })
			.json<{
				latitude?: string;
				longitude?: string;
			}>();
		const lat = res?.latitude ? Number(res.latitude) : undefined;
		const lng = res?.longitude ? Number(res.longitude) : undefined;
		if (
			typeof lat === 'number' &&
			!Number.isNaN(lat) &&
			typeof lng === 'number' &&
			!Number.isNaN(lng)
		) {
			return { lat, lng };
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Get the user's current location using the browser's geolocation API
 */
export async function getCurrentLocation(
	options: LocationOptions = {},
): Promise<Coords> {
	if (!isGeolocationSupported()) {
		throw new LocationError(
			'NOT_SUPPORTED',
			'Geolocation is not supported by this browser',
		);
	}

	const {
		enableHighAccuracy = true,
		timeout = 10000,
		maximumAge = 300000,
		grantedOnly = false,
		detection = 'user',
	} = options;

	const GEO_ERROR_MESSAGE: Record<LocationErrorCode, string> = {
		PERMISSION_DENIED: 'Location access denied by user',
		PROMPT_REQUIRED: 'Location permission not granted',
		POSITION_UNAVAILABLE: 'Location information unavailable',
		TIMEOUT: 'Location request timed out',
		NOT_SUPPORTED: 'Geolocation is not supported by this browser',
		UNKNOWN_ERROR: 'An unknown error occurred',
	};

	// IP-only strategy
	if (detection === 'ip') {
		const ip = await getLocationFromIp();
		if (ip) return ip;
		throw new LocationError(
			'POSITION_UNAVAILABLE',
			GEO_ERROR_MESSAGE.POSITION_UNAVAILABLE,
		);
	}

	// User-based strategy (with optional IP fallback)
	const tryUserLocation = async (): Promise<Coords> => {
		// If grantedOnly is requested, preflight the permission and bail if not granted
		if (grantedOnly) {
			const status = await getPermissionStatus();
			if (status !== 'granted') {
				const code: LocationErrorCode =
					status === 'denied' ? 'PERMISSION_DENIED' : 'PROMPT_REQUIRED';
				throw new LocationError(code, GEO_ERROR_MESSAGE[code]);
			}
		}

		return new Promise((resolve, reject) => {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const location: Coords = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					};
					resolve(location);
				},
				(error) => {
					const map: Record<number, LocationErrorCode> = {
						[error.PERMISSION_DENIED]: 'PERMISSION_DENIED',
						[error.POSITION_UNAVAILABLE]: 'POSITION_UNAVAILABLE',
						[error.TIMEOUT]: 'TIMEOUT',
					};
					const code = map[error.code] ?? 'UNKNOWN_ERROR';
					reject(new LocationError(code, GEO_ERROR_MESSAGE[code]));
				},
				{
					enableHighAccuracy,
					timeout,
					maximumAge,
				},
			);
		});
	};

	if (detection === 'ip-fallback') {
		try {
			return await tryUserLocation();
		} catch (error) {
			const ip = await getLocationFromIp();
			if (ip) return ip;
			// fallthrough to rethrow a consistent error
			throw error;
		}
	}

	// default 'user'
	return tryUserLocation();

	// unreachable
}

/**
 * type guard to check if the location extends Coords
 */
export function isCoords(location: unknown): location is Coords {
	// must be a non-null object with finite numeric lat/lng
	if (location === null || typeof location !== 'object') return false;
	if ('lat' in location && 'lng' in location) {
		return true;
	}
	return false;
}

export function isFormattedAddress(
	location: unknown,
): location is FormattedAddress {
	return !!(
		location &&
		typeof location === 'object' &&
		'mainText' in location &&
		'formattedAddress' in location
	);
}

/**
 * Parse Google's address_components into main/secondary format
 */
export function parseAddressComponents(
	formattedAddress: string,
): FormattedAddress {
	const main = [];
	const secondary = [];
	const parts = formattedAddress.split(/,\s*/);
	let length = 0;
	for (const part of parts) {
		if (main.length === 0 || length + part.length < 50) {
			main.push(part);
			length += part.length;
		} else {
			secondary.push(part);
		}
	}

	return {
		mainText: main.join(', '),
		secondaryText: secondary.join(', '),
		formattedAddress: formattedAddress,
	};
}

/**
 * Convert coordinates to a human-readable address using Google's Geocoding API
 */
export async function reverseGeocode(
	location: Coords,
	options: Partial<LocationConfig> = {},
): Promise<{ address: FormattedAddress; placeId: string } | null> {
	try {
		const apiKey = getApiKey(options.apiKey);
		const response = await fetch(
			`https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.lat},${location.lng}&key=${apiKey}`,
		);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = (await response.json()) as google.maps.GeocoderResponse & {
			status: string;
		};

		const [result] = data.results || [];
		if (data.status === 'OK' && result) {
			const formattedAddress = result.formatted_address || '';
			const placeId = result.place_id;

			return {
				address: parseAddressComponents(formattedAddress),
				placeId,
			};
		}

		return null;
	} catch {
		return null;
	}
}
